/**
 * TrialBalanceView — single-page proof listing every account with debit vs
 * credit totals + a green/red banner showing whether the ledger is balanced.
 */
import { Fragment } from "react";
import { Scale, CheckCircle2, AlertTriangle } from "lucide-react";
import { ReportShell } from "./atoms";
import { fmtMoney } from "@/lib/format";

const GROUP_LABEL = {
  income:  "Income",
  cogs:    "Cost of Goods Sold",
  expense: "Operating Expenses",
  other:   "Other",
};
const GROUP_BG = {
  income:  "#DCFCE7",
  cogs:    "#FFEDD5",
  expense: "#FEE2E2",
  other:   "#E0E7FF",
};
const GROUP_FG = {
  income:  "#166534",
  cogs:    "#9A3412",
  expense: "#991B1B",
  other:   "#3730A3",
};


export default function TrialBalanceView({ tb, onExport, onShare }) {
  // Group rows for visual sub-headers
  const groups = ["income", "cogs", "expense", "other"];
  const byGroup = groups.map((g) => ({
    g,
    rows: (tb.rows || []).filter((r) => r.group === g),
  })).filter((g) => g.rows.length > 0);

  return (
    <ReportShell title="Trial Balance" kind="trial-balance" onExport={onExport} onShare={onShare} testid="tb-view">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="tb-summary">
        <Stat label="Accounts" value={tb.account_count} />
        <Stat label="Total debits" value={fmtMoney(tb.total_debit)} tone="warm" />
        <Stat label="Total credits" value={fmtMoney(tb.total_credit)} tone="cool" />
        <Stat
          label="Difference"
          value={fmtMoney(tb.difference)}
          tone={tb.balanced ? "good" : "bad"}
        />
      </div>

      {/* Balance banner */}
      <div
        className={`border-l-4 px-4 py-3 mb-5 flex items-center gap-2 text-sm ${
          tb.balanced ? "border-green-600 bg-green-50 text-green-800" : "border-red-500 bg-red-50 text-red-700"
        }`}
        data-testid="tb-balance-banner"
      >
        {tb.balanced ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
        <div>
          {tb.balanced ? (
            <>
              <strong>Debits = Credits</strong> — the ledger is balanced. {fmtMoney(tb.total_debit)} on each side.
            </>
          ) : (
            <>
              <strong>Imbalance of {fmtMoney(Math.abs(tb.difference))}</strong> —
              {" "}review categorisation and account scope. The GL running balance will drift until fixed.
            </>
          )}
        </div>
      </div>

      {/* Grouped table */}
      <div className="border overflow-x-auto" style={{ borderColor: "var(--ll-border)" }} data-testid="tb-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0b5d9e] text-white">
            <tr className="text-[10px] uppercase tracking-widest">
              <th className="px-4 py-2.5 text-left">Account</th>
              <th className="px-4 py-2.5 text-right w-32">Debit</th>
              <th className="px-4 py-2.5 text-right w-32">Credit</th>
            </tr>
          </thead>
          <tbody>
            {byGroup.map(({ g, rows }) => {
              const gTotal = rows.reduce(
                (s, r) => ({ debit: s.debit + r.debit, credit: s.credit + r.credit }),
                { debit: 0, credit: 0 },
              );
              return (
                <Fragment key={g}>
                  <tr className="border-t" style={{ borderColor: "var(--ll-border)" }}>
                    <td
                      colSpan={3}
                      className="px-4 py-1.5 text-[10px] uppercase tracking-widest font-bold"
                      style={{ background: GROUP_BG[g], color: GROUP_FG[g] }}
                      data-testid={`tb-group-${g}`}
                    >
                      {GROUP_LABEL[g]}
                    </td>
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.category} className="border-t hover:bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`tb-row-${r.category}`}>
                      <td className="px-4 py-1.5 text-sm">{r.category}</td>
                      <td className="px-4 py-1.5 text-right font-mono-fin text-sm text-green-700">
                        {r.debit > 0 ? fmtMoney(r.debit) : ""}
                      </td>
                      <td className="px-4 py-1.5 text-right font-mono-fin text-sm text-red-700">
                        {r.credit > 0 ? fmtMoney(r.credit) : ""}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }}>
                    <td className="px-4 py-1.5 text-xs font-bold">Subtotal · {GROUP_LABEL[g]}</td>
                    <td className="px-4 py-1.5 text-right font-mono-fin text-sm font-bold text-green-700">{fmtMoney(gTotal.debit)}</td>
                    <td className="px-4 py-1.5 text-right font-mono-fin text-sm font-bold text-red-700">{fmtMoney(gTotal.credit)}</td>
                  </tr>
                </Fragment>
              );
            })}
            {/* Grand total */}
            <tr className="border-t-2 bg-[#0b5d9e] text-white" style={{ borderColor: "#0b5d9e" }}>
              <td className="px-4 py-2.5 text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                <Scale size={13} /> Grand totals
              </td>
              <td className="px-4 py-2.5 text-right font-mono-fin font-bold">{fmtMoney(tb.total_debit)}</td>
              <td className="px-4 py-2.5 text-right font-mono-fin font-bold">{fmtMoney(tb.total_credit)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </ReportShell>
  );
}


function Stat({ label, value, tone }) {
  const bg = {
    warm: "#FFF7ED", cool: "#EFF6FF", good: "#DCFCE7", bad: "#FEE2E2", default: "#F9FAFB",
  }[tone || "default"];
  const fg = {
    warm: "#9A3412", cool: "#1E40AF", good: "#166534", bad: "#991B1B", default: "#1F2937",
  }[tone || "default"];
  return (
    <div className="border p-3" style={{ background: bg, borderColor: "var(--ll-border)" }}>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: fg, opacity: 0.75 }}>{label}</div>
      <div className="font-mono-fin text-lg font-semibold" style={{ color: fg }}>{value}</div>
    </div>
  );
}
